"use client";

import { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react";

import { CloseIcon, SVG } from "@saas/shared/icon";
import { classNames } from "@saas/shared/utils/class-names/class-names";

import { TypographyProps } from "../typography/typography";

const Variant = {
  POSITIVE: "positive",
  STANDBY: "standby",
  NEUTRAL: "neutral",
  ERROR: "error",
  WARNING: "warning",
  URGENT: "urgent",
  CAUTION: "caution",
  TEXT: "text",
} as const;

const Size = {
  SMALL: "small",
  BIG: "big",
} as const;

const Align = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right",
} as const;

// TODO: refactor to use children for more flexibility
export interface LabelProps extends ComponentPropsWithoutRef<"span"> {
  variant: (typeof Variant)[keyof typeof Variant];
  size?: (typeof Size)[keyof typeof Size];
  align?: (typeof Align)[keyof typeof Align];
  value: string | ReactNode;
  testid?: string;
  onClose?: () => void;
  icon?: SVG;
}

export const Label = forwardRef<HTMLSpanElement, LabelProps>(
  (
    {
      value,
      variant,
      size = "small",
      align = "center",
      testid,
      onClose,
      icon: Icon,
      ...props
    }: LabelProps,
    ref
  ) => {
    const labelColor = {
      [Variant.POSITIVE]: "bg-green-G100 text-positive",
      [Variant.STANDBY]: "bg-blue-B100 text-button",
      [Variant.NEUTRAL]: "bg-neutral-N100 text-le",
      [Variant.ERROR]: "bg-red-R100 text-error",
      [Variant.WARNING]: "bg-orange-O100 text-orange-O700",
      [Variant.URGENT]: "bg-purple-P100 text-purple-P700",
      [Variant.CAUTION]: "bg-yellow-Y100 text-yellow-Y800",
      [Variant.TEXT]: "bg-white text-le border border-neutral-N200",
    };

    const sizeClass = {
      [Size.SMALL]: "px-2 py-1",
      [Size.BIG]: "px-2.5 py-0.5",
    };

    const labelType: Record<string, TypographyProps["type"]> = {
      [Size.SMALL]: "label-medium",
      [Size.BIG]: "label-large",
    };

    const alignClass = {
      [Align.LEFT]: "justify-start",
      [Align.CENTER]: "justify-center",
      [Align.RIGHT]: "justify-end",
    };

    return (
      <span
        {...props}
        ref={ref}
        className={classNames(
          "inline-flex items-center rounded",
          labelColor[variant],
          sizeClass[size],
          alignClass[align],
          labelType[size],
          props.className
        )}
        data-testid={testid}
      >
        {Icon ? (
          <Icon
            className={classNames("mr-1 h-4 w-4 shrink-0", labelColor[variant])}
          />
        ) : null}

        {value}

        {onClose ? (
          <button onClick={onClose}>
            <span className={"sr-only"}>Close label</span>
            <CloseIcon className={"ml-1 h-4 w-4"} />
          </button>
        ) : null}
      </span>
    );
  }
);

Label.displayName = "Label";

export default Label;
